import React, { useRef, useState, useEffect, useCallback } from 'react';

interface DrawingCanvasProps {
  onSaveDrawing?: (imageData: string) => void;
  className?: string;
  initialObjects?: DrawnObject[];
  onObjectsChange?: (objects: DrawnObject[]) => void;
}

interface DrawingTool {
  type: 'hand' | 'select' | 'pen' | 'line' | 'rectangle' | 'circle' | 'arrow' | 'text' | 'diamond' | 'image' | 'eraser';
  color: string;
  size: number;
}

interface Point {
  x: number;
  y: number;
}

interface DrawnObject {
  id: string;
  type: string;
  points?: Point[];
  start?: Point;
  end?: Point;
  color: string;
  size: number;
  text?: string;
  selected?: boolean;
  bounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ onSaveDrawing, className = '', initialObjects = [], onObjectsChange }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [, setLastSaveTime] = useState<number>(Date.now());
  const [tool, setTool] = useState<DrawingTool>({
    type: 'hand',
    color: '#ffffff',
    size: 2
  });
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [objects, setObjects] = useState<DrawnObject[]>(initialObjects);
  const [currentPath, setCurrentPath] = useState<Point[]>([]);
  const [selectedObjects, setSelectedObjects] = useState<string[]>([]);
  const [selectionBox, setSelectionBox] = useState<{start: Point, end: Point} | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [panStart, setPanStart] = useState<Point | null>(null);
  const [canvasOffset, setCanvasOffset] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState<Point | null>(null);
  const [textScreenPosition, setTextScreenPosition] = useState<Point | null>(null);
  const [history, setHistory] = useState<DrawnObject[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [previewPoint, setPreviewPoint] = useState<Point | null>(null);
  const [resizing, setResizing] = useState<{objectId: string, handle: string} | null>(null);
  const [resizeStart, setResizeStart] = useState<Point | null>(null);

  // Initialize canvas and handle resizing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      redrawCanvas();
    };

    // Initial setup
    updateCanvasSize();

    // Handle window resize
    const handleResize = () => {
      updateCanvasSize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save to history when objects change
  useEffect(() => {
    if (objects.length > 0 || historyIndex === -1) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push([...objects]);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects]);

  // Notify parent when objects change
  useEffect(() => {
    if (onObjectsChange) {
      onObjectsChange(objects);
    }
  }, [objects, onObjectsChange]);

  const getMousePos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    // Get canvas coordinates
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    // Convert to world coordinates (accounting for zoom and pan)
    return {
      x: (canvasX - canvasOffset.x) / zoom,
      y: (canvasY - canvasOffset.y) / zoom
    };
  }, [canvasOffset, zoom]);

  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // If user is currently typing, don't start new drawing
    if (isTyping) {
      return;
    }
    
    const point = getMousePos(e);
    setIsDrawing(true);
    setStartPoint(point);
    setDragStart(point);
    
    if (tool.type === 'hand') {
      setIsDragging(true);
      // Store screen coordinates for panning
      const rect = canvasRef.current!.getBoundingClientRect();
      setPanStart({ 
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      return;
    }
    
    if (tool.type === 'select') {
      const clickedObject = findObjectAtPoint(point);
      
      // Check if clicking on a resize handle of an already selected object
      const resizeHandle = selectedObjects.length > 0 ? findResizeHandle(point) : null;
      if (resizeHandle && clickedObject && selectedObjects.includes(clickedObject.id)) {
        setResizing(resizeHandle);
        setResizeStart(point);
        return;
      }
      
      // Check if clicking within the selection bounds (for multi-object dragging)
      const clickingInSelectionBounds = selectedObjects.length > 0 ? isPointInSelectionBounds(point) : false;
      
      if (clickedObject) {
        // Handle object selection
        if (e.ctrlKey || e.metaKey) {
          // Multi-select with Ctrl/Cmd
          setSelectedObjects(prev => 
            prev.includes(clickedObject.id) 
              ? prev.filter(id => id !== clickedObject.id)
              : [...prev, clickedObject.id]
          );
        } else if (!selectedObjects.includes(clickedObject.id)) {
          // Single select only if the clicked object is not already selected
          setSelectedObjects([clickedObject.id]);
        }
        // If clicking on an already selected object, keep the current selection for dragging
        setIsDragging(true);
      } else if (clickingInSelectionBounds && selectedObjects.length > 0) {
        // Clicking within the bounds of selected objects - drag all of them
        setIsDragging(true);
      } else {
        // Start selection box if not clicking on object or within selection bounds
        if (!e.ctrlKey && !e.metaKey) {
          setSelectedObjects([]);
        }
        setSelectionBox({ start: point, end: point });
        setIsDragging(true);
      }
      return;
    }
    
    if (tool.type === 'text') {
      setTextPosition(point);
      // Store screen coordinates for stable positioning
      const rect = canvasRef.current!.getBoundingClientRect();
      setTextScreenPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsTyping(true);
      return;
    }
    
    if (tool.type === 'pen') {
      setCurrentPath([point]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMousePos, tool.type, isTyping]);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    
    const currentPoint = getMousePos(e);
    
    if (tool.type === 'hand' && isDragging && panStart) {
      // Simple pan using screen coordinates
      const rect = canvasRef.current!.getBoundingClientRect();
      const currentScreenX = e.clientX - rect.left;
      const currentScreenY = e.clientY - rect.top;
      
      const dx = currentScreenX - panStart.x;
      const dy = currentScreenY - panStart.y;
      
      setCanvasOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setPanStart({ x: currentScreenX, y: currentScreenY });
      return;
    }
    
    if (tool.type === 'select') {
      if (resizing && resizeStart) {
        // Handle resizing
        const dx = currentPoint.x - resizeStart.x;
        const dy = currentPoint.y - resizeStart.y;
        resizeObject(resizing.objectId, resizing.handle, dx, dy);
        setResizeStart(currentPoint);
        return;
      } else if (selectionBox) {
        // Update selection box
        setSelectionBox(prev => prev ? { ...prev, end: currentPoint } : null);
        return;
      } else if (isDragging && selectedObjects.length > 0 && dragStart) {
        // Move selected objects
        const dx = currentPoint.x - dragStart.x;
        const dy = currentPoint.y - dragStart.y;
        moveSelectedObjects(dx, dy);
        setDragStart(currentPoint);
        return;
      }
    }
    
    if (tool.type === 'pen') {
      setCurrentPath(prev => [...prev, currentPoint]);
      // Redraw canvas to show current drawing
      redrawCanvas();
    } else if (tool.type !== 'hand' && tool.type !== 'select' && tool.type !== 'text') {
      // Update preview point for shape tools
      setPreviewPoint(currentPoint);
      redrawCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing, getMousePos, tool, isDragging, selectedObjects, dragStart, zoom, canvasOffset, selectionBox, resizing]);

  const stopDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    
    const endPoint = getMousePos(e);
    
    if (tool.type === 'hand') {
      setIsDragging(false);
      setIsDrawing(false);
      return;
    }
    
    if (tool.type === 'select') {
      if (resizing) {
        setResizing(null);
        setResizeStart(null);
      } else if (selectionBox && startPoint) {
        // Complete selection box - select objects within box
        const finalSelectionBox = { start: startPoint, end: endPoint };
        const selectedIds = getObjectsInSelectionBox(finalSelectionBox);
        if (e.ctrlKey || e.metaKey) {
          // Add to existing selection
          setSelectedObjects(prev => [...new Set([...prev, ...selectedIds])]);
        } else {
          // Replace selection
          setSelectedObjects(selectedIds);
        }
        setSelectionBox(null);
      }
      setIsDragging(false);
      setIsDrawing(false);
      return;
    }
    
    if (!startPoint) {
      setIsDrawing(false);
      return;
    }
    
    // Create new object
    const newObject: DrawnObject = {
      id: `obj_${Date.now()}`,
      type: tool.type,
      color: tool.color,
      size: tool.size
    };
    
    switch (tool.type) {
      case 'pen':
        if (currentPath.length > 0) {
          newObject.points = [...currentPath];
        }
        break;
      case 'line':
      case 'arrow':
      case 'rectangle':
      case 'circle':
      case 'diamond':
        newObject.start = startPoint;
        newObject.end = endPoint;
        break;
      case 'text':
        if (startPoint) {
          newObject.start = startPoint;
          newObject.text = ''; // Will be set by text input
        }
        break;
      case 'image':
      case 'eraser':
        // These tools might need different handling in the future
        newObject.start = startPoint;
        newObject.end = endPoint;
        break;
    }
    
    if (newObject.points?.length || (newObject.start && newObject.end)) {
      setObjects(prev => [...prev, newObject]);
      
      // Auto-switch to selection tool if unlocked (this code only runs for drawing tools)
      if (!isLocked) {
        setTool(prev => ({ ...prev, type: 'select' }));
      }
    }
    
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPath([]);
    setIsDragging(false);
    setPreviewPoint(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing, getMousePos, startPoint, tool, currentPath, isLocked]);

  const drawArrow = (ctx: CanvasRenderingContext2D, start: Point, end: Point, color: string, size: number) => {
    const headLength = 20;
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    
    // Draw line
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    
    // Draw arrowhead
    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
      end.x - headLength * Math.cos(angle - Math.PI / 6),
      end.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
      end.x - headLength * Math.cos(angle + Math.PI / 6),
      end.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  };

  // Helper functions
  const drawLine = (ctx: CanvasRenderingContext2D, start: Point, end: Point, color: string, size: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
  };
  
  const findObjectAtPoint = (point: Point): DrawnObject | null => {
    for (let i = objects.length - 1; i >= 0; i--) {
      const obj = objects[i];
      if (isPointInObject(point, obj)) {
        return obj;
      }
    }
    return null;
  };

  const getSelectionBounds = () => {
    if (selectedObjects.length === 0) return null;
    
    const selectedObjs = objects.filter(obj => selectedObjects.includes(obj.id));
    if (selectedObjs.length === 0) return null;
    
    // Calculate the bounding box of all selected objects
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    
    selectedObjs.forEach(obj => {
      if (obj.points) {
        // For pen strokes
        obj.points.forEach(p => {
          minX = Math.min(minX, p.x);
          maxX = Math.max(maxX, p.x);
          minY = Math.min(minY, p.y);
          maxY = Math.max(maxY, p.y);
        });
      } else if (obj.start && obj.end) {
        // For shapes
        minX = Math.min(minX, obj.start.x, obj.end.x);
        maxX = Math.max(maxX, obj.start.x, obj.end.x);
        minY = Math.min(minY, obj.start.y, obj.end.y);
        maxY = Math.max(maxY, obj.start.y, obj.end.y);
      }
    });
    
    // Add padding
    const padding = 5;
    return {
      left: minX - padding,
      right: maxX + padding, 
      top: minY - padding,
      bottom: maxY + padding
    };
  };

  const isPointInSelectionBounds = (point: Point): boolean => {
    const bounds = getSelectionBounds();
    if (!bounds) return false;
    
    // Convert to numbers explicitly to avoid any type coercion issues
    const pointX = Number(point.x);
    const pointY = Number(point.y);
    const left = Number(bounds.left);
    const right = Number(bounds.right);
    const top = Number(bounds.top);
    const bottom = Number(bounds.bottom);
    
    // Check if point is within bounds
    const withinX = (pointX >= left) && (pointX <= right);
    const withinY = (pointY >= top) && (pointY <= bottom);
    
    return withinX && withinY;
  };


  const findResizeHandle = (point: Point): {objectId: string, handle: string} | null => {
    for (const objId of selectedObjects) {
      const obj = objects.find(o => o.id === objId);
      if (!obj) continue;

      let bounds: { x: number, y: number, width: number, height: number } | null = null;

      if (obj.points && obj.points.length > 0) {
        const minX = Math.min(...obj.points.map(p => p.x));
        const maxX = Math.max(...obj.points.map(p => p.x));
        const minY = Math.min(...obj.points.map(p => p.y));
        const maxY = Math.max(...obj.points.map(p => p.y));
        bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
      } else if (obj.start && obj.end) {
        const minX = Math.min(obj.start.x, obj.end.x);
        const maxX = Math.max(obj.start.x, obj.end.x);
        const minY = Math.min(obj.start.y, obj.end.y);
        const maxY = Math.max(obj.start.y, obj.end.y);
        bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
      }

      if (bounds) {
        const tolerance = 8;

        // Check each corner handle
        const handles = [
          { name: 'top-left', x: bounds.x, y: bounds.y },
          { name: 'top-right', x: bounds.x + bounds.width, y: bounds.y },
          { name: 'bottom-left', x: bounds.x, y: bounds.y + bounds.height },
          { name: 'bottom-right', x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        ];

        for (const handle of handles) {
          if (Math.abs(point.x - handle.x) <= tolerance && Math.abs(point.y - handle.y) <= tolerance) {
            return { objectId: objId, handle: handle.name };
          }
        }
      }
    }
    return null;
  };
  
  const isPointInObject = (point: Point, obj: DrawnObject): boolean => {
    if (obj.points) {
      // Check if point is near pen path
      return obj.points.some(p => 
        Math.abs(p.x - point.x) < 15 && Math.abs(p.y - point.y) < 15
      );
    }
    if (obj.start && obj.end) {
      const { start, end } = obj;
      if (obj.type === 'rectangle') {
        const minX = Math.min(start.x, end.x);
        const maxX = Math.max(start.x, end.x);
        const minY = Math.min(start.y, end.y);
        const maxY = Math.max(start.y, end.y);
        
        // Check if point is inside rectangle OR near the border (stroke detection)
        const tolerance = 10;
        const isInside = point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
        const isNearBorder = (
          (Math.abs(point.x - minX) <= tolerance && point.y >= minY - tolerance && point.y <= maxY + tolerance) ||
          (Math.abs(point.x - maxX) <= tolerance && point.y >= minY - tolerance && point.y <= maxY + tolerance) ||
          (Math.abs(point.y - minY) <= tolerance && point.x >= minX - tolerance && point.x <= maxX + tolerance) ||
          (Math.abs(point.y - maxY) <= tolerance && point.x >= minX - tolerance && point.x <= maxX + tolerance)
        );
        
        return isInside || isNearBorder;
      }
      if (obj.type === 'circle') {
        const centerX = (start.x + end.x) / 2;
        const centerY = (start.y + end.y) / 2;
        const radius = Math.sqrt(
          Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        ) / 2;
        const distance = Math.sqrt(
          Math.pow(point.x - centerX, 2) + Math.pow(point.y - centerY, 2)
        );
        // Check if point is on the circle stroke (within tolerance)
        return Math.abs(distance - radius) <= 15;
      }
      // For lines, arrows - check distance to line
      const tolerance = 15;
      if (obj.type === 'line' || obj.type === 'arrow') {
        const lineLength = Math.sqrt(
          Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        );
        if (lineLength === 0) return false;
        
        const t = Math.max(0, Math.min(1, 
          ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) / (lineLength * lineLength)
        ));
        const projection = {
          x: start.x + t * (end.x - start.x),
          y: start.y + t * (end.y - start.y)
        };
        const distance = Math.sqrt(
          Math.pow(point.x - projection.x, 2) + Math.pow(point.y - projection.y, 2)
        );
        return distance <= tolerance;
      }
    }
    if (obj.text && obj.start) {
      // For text objects, check if point is near text position
      return Math.abs(point.x - obj.start.x) < 50 && Math.abs(point.y - obj.start.y) < 20;
    }
    return false;
  };
  
  // Pure helper – returns a *new* object translated by (dx, dy)
  const translateObject = (obj: DrawnObject, dx: number, dy: number): DrawnObject => {
    if (obj.points) {
      return { ...obj, points: obj.points.map(p => ({ x: p.x + dx, y: p.y + dy })) };
    }
    if (obj.start && obj.end) {
      return {
        ...obj,
        start: { x: obj.start.x + dx, y: obj.start.y + dy },
        end:   { x: obj.end.x   + dx, y: obj.end.y   + dy }
      };
    }
    if (obj.text && obj.start) {
      return {
        ...obj,
        start: { x: obj.start.x + dx, y: obj.start.y + dy },
        end:   obj.end ? { x: obj.end.x + dx, y: obj.end.y + dy } : undefined
      };
    }
    return obj;
  };

  const moveSelectedObjects = (dx: number, dy: number) => {
    // Single setObjects call; every selected id is translated in one pass
    setObjects(prev =>
      prev.map(obj => selectedObjects.includes(obj.id)
        ? translateObject(obj, dx, dy)
        : obj
      )
    );
  };

  const resizeObject = (objectId: string, handle: string, dx: number, dy: number) => {
    setObjects(prev => prev.map(obj => {
      if (obj.id !== objectId) return obj;

      if (obj.points) {
        // For pen strokes, resize by scaling all points relative to the bounding box
        const minX = Math.min(...obj.points.map(p => p.x));
        const maxX = Math.max(...obj.points.map(p => p.x));
        const minY = Math.min(...obj.points.map(p => p.y));
        const maxY = Math.max(...obj.points.map(p => p.y));
        const width = maxX - minX;
        const height = maxY - minY;

        let scaleX = 1, scaleY = 1;
        let offsetX = 0, offsetY = 0;

        if (handle.includes('right')) {
          scaleX = width > 0 ? (width + dx) / width : 1;
        } else if (handle.includes('left')) {
          scaleX = width > 0 ? (width - dx) / width : 1;
          offsetX = dx;
        }

        if (handle.includes('bottom')) {
          scaleY = height > 0 ? (height + dy) / height : 1;
        } else if (handle.includes('top')) {
          scaleY = height > 0 ? (height - dy) / height : 1;
          offsetY = dy;
        }

        return {
          ...obj,
          points: obj.points.map(p => ({
            x: (p.x - minX) * Math.max(0.1, scaleX) + minX + offsetX,
            y: (p.y - minY) * Math.max(0.1, scaleY) + minY + offsetY
          }))
        };
      }

      if (obj.start && obj.end) {
        const newObj = { ...obj };
        
        switch (handle) {
          case 'top-left':
            newObj.start = { x: obj.start.x + dx, y: obj.start.y + dy };
            break;
          case 'top-right':
            newObj.start = { x: obj.start.x, y: obj.start.y + dy };
            newObj.end = { x: obj.end.x + dx, y: obj.end.y };
            break;
          case 'bottom-left':
            newObj.start = { x: obj.start.x + dx, y: obj.start.y };
            newObj.end = { x: obj.end.x, y: obj.end.y + dy };
            break;
          case 'bottom-right':
            newObj.end = { x: obj.end.x + dx, y: obj.end.y + dy };
            break;
        }
        
        return newObj;
      }

      return obj;
    }));
  };

  const getObjectsInSelectionBox = (box: {start: Point, end: Point}): string[] => {
    const selectionMinX = Math.min(box.start.x, box.end.x);
    const selectionMaxX = Math.max(box.start.x, box.end.x);
    const selectionMinY = Math.min(box.start.y, box.end.y);
    const selectionMaxY = Math.max(box.start.y, box.end.y);

    // Add minimum selection area to avoid zero-area selections
    const MIN_SELECTION_SIZE = 5;
    const effectiveMaxX = Math.max(selectionMaxX, selectionMinX + MIN_SELECTION_SIZE);
    const effectiveMaxY = Math.max(selectionMaxY, selectionMinY + MIN_SELECTION_SIZE);

    return objects.filter(obj => {
      if (obj.points) {
        // For pen strokes, check if any point is in selection box
        return obj.points.some(p => 
          p.x >= selectionMinX && p.x <= effectiveMaxX && 
          p.y >= selectionMinY && p.y <= effectiveMaxY
        );
      }
      
      if (obj.start && obj.end) {
        // For shapes, check if the object's bounding box intersects with selection box
        const objectMinX = Math.min(obj.start.x, obj.end.x);
        const objectMaxX = Math.max(obj.start.x, obj.end.x);
        const objectMinY = Math.min(obj.start.y, obj.end.y);
        const objectMaxY = Math.max(obj.start.y, obj.end.y);
        
        // Check for intersection between two rectangles
        return !(objectMaxX < selectionMinX || 
                objectMinX > effectiveMaxX || 
                objectMaxY < selectionMinY || 
                objectMinY > effectiveMaxY);
      }
      
      if (obj.text && obj.start) {
        // For text objects, check if text position is within selection
        return obj.start.x >= selectionMinX && obj.start.x <= effectiveMaxX &&
               obj.start.y >= selectionMinY && obj.start.y <= effectiveMaxY;
      }
      
      return false;
    }).map(obj => obj.id);
  };

  const clearCanvas = () => {
    if (objects.length > 0) {
      setShowClearConfirm(true);
    }
    setIsMenuOpen(false); // Close menu after action
  };

  const confirmClearCanvas = () => {
    setObjects([]);
    setSelectedObjects([]);
    setSelectionBox(null);
    setShowClearConfirm(false);
    clearSavedData(); // Clear saved content when canvas is cleared
    redrawCanvas();
  };

  const cancelClearCanvas = () => {
    setShowClearConfirm(false);
  };

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
    setIsMenuOpen(false); // Close menu after selection
    
    const newColor = newTheme === 'light' ? '#000000' : '#ffffff';
    
    // Update tool color based on theme
    setTool(prev => ({
      ...prev,
      color: newColor
    }));
    
    // Update all existing objects to use the new theme color
    setObjects(prev => prev.map(obj => ({
      ...obj,
      color: newColor
    })));
    
    // Redraw canvas with new theme will happen via useEffect
  };

  // Auto-save functionality
  const saveCanvasData = useCallback(() => {
    try {
      const canvasData = {
        objects,
        theme,
        tool,
        timestamp: Date.now()
      };
      localStorage.setItem('drawing-canvas-autosave', JSON.stringify(canvasData));
      setLastSaveTime(Date.now());
      console.log('Canvas auto-saved');
    } catch (error) {
      console.error('Failed to auto-save canvas:', error);
    }
  }, [objects, theme, tool]);

  const loadCanvasData = useCallback(() => {
    try {
      const saved = localStorage.getItem('drawing-canvas-autosave');
      if (saved) {
        const canvasData = JSON.parse(saved);
        if (canvasData.objects && Array.isArray(canvasData.objects)) {
          setObjects(canvasData.objects);
          if (canvasData.theme) {
            setTheme(canvasData.theme);
          }
          console.log('Canvas data loaded from auto-save');
        }
      }
    } catch (error) {
      console.error('Failed to load canvas data:', error);
    }
  }, []);

  const clearSavedData = useCallback(() => {
    try {
      localStorage.removeItem('drawing-canvas-autosave');
      console.log('Saved canvas data cleared');
    } catch (error) {
      console.error('Failed to clear saved data:', error);
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _saveDrawing = () => {
    const canvas = canvasRef.current;
    if (canvas && onSaveDrawing) {
      const imageData = canvas.toDataURL('image/png');
      onSaveDrawing(imageData);
    }
  };
  
  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setObjects(history[historyIndex - 1] || []);
    }
  };
  
  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setObjects(history[historyIndex + 1] || []);
    }
  };
  
  const zoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  };
  
  const zoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.2));
  };
  
  const resetZoom = () => {
    setZoom(1);
    setCanvasOffset({ x: 0, y: 0 });
  };

  const addText = () => {
    if (textInput.trim() && textPosition) {
      const newObject: DrawnObject = {
        id: `text_${Date.now()}`,
        type: 'text',
        start: textPosition,
        end: textPosition,
        color: tool.color,
        size: tool.size,
        text: textInput.trim()
      };
      setObjects(prev => [...prev, newObject]);
      setTextInput('');
      setTextPosition(null);
      setTextScreenPosition(null);
      setIsTyping(false);
    }
  };

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Save current transform
    ctx.save();
    
    // Reset transform and clear
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = theme === 'light' ? '#ffffff' : '#2d2d2d';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Apply zoom and pan
    ctx.translate(canvasOffset.x, canvasOffset.y);
    ctx.scale(zoom, zoom);
    
    // Set drawing styles
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Draw all objects
    objects.forEach(obj => {
      const isSelected = selectedObjects.includes(obj.id);
      
      ctx.strokeStyle = obj.color;
      ctx.lineWidth = obj.size;
      
      if (isSelected) {
        ctx.strokeStyle = '#00aaff';
        ctx.lineWidth = obj.size + 2;
      }
      
      switch (obj.type) {
        case 'pen':
          if (obj.points && obj.points.length > 1) {
            ctx.beginPath();
            ctx.moveTo(obj.points[0].x, obj.points[0].y);
            obj.points.forEach(point => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;
          
        case 'line':
          if (obj.start && obj.end) {
            drawLine(ctx, obj.start, obj.end, obj.color, obj.size);
          }
          break;
          
        case 'arrow':
          if (obj.start && obj.end) {
            drawArrow(ctx, obj.start, obj.end, obj.color, obj.size);
          }
          break;
          
        case 'rectangle':
          if (obj.start && obj.end) {
            const width = obj.end.x - obj.start.x;
            const height = obj.end.y - obj.start.y;
            ctx.strokeRect(obj.start.x, obj.start.y, width, height);
          }
          break;
          
        case 'circle':
          if (obj.start && obj.end) {
            const radius = Math.sqrt(
              Math.pow(obj.end.x - obj.start.x, 2) + Math.pow(obj.end.y - obj.start.y, 2)
            ) / 2; // Use half the distance as radius
            const centerX = (obj.start.x + obj.end.x) / 2;
            const centerY = (obj.start.y + obj.end.y) / 2;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            ctx.stroke();
          }
          break;
          
        case 'diamond':
          if (obj.start && obj.end) {
            const centerX = (obj.start.x + obj.end.x) / 2;
            const centerY = (obj.start.y + obj.end.y) / 2;

            ctx.beginPath();
            ctx.moveTo(centerX, obj.start.y); // Top point
            ctx.lineTo(obj.end.x, centerY);   // Right point
            ctx.lineTo(centerX, obj.end.y);   // Bottom point
            ctx.lineTo(obj.start.x, centerY); // Left point
            ctx.closePath();
            ctx.stroke();
          }
          break;
          
        case 'text':
          if (obj.start && obj.text) {
            ctx.fillStyle = obj.color;
            ctx.font = `${obj.size * 8}px Arial`;
            ctx.fillText(obj.text, obj.start.x, obj.start.y);
          }
          break;
      }
    });
    
    // Draw current path being drawn
    if (currentPath.length > 1 && tool.type === 'pen') {
      ctx.strokeStyle = tool.color;
      ctx.lineWidth = tool.size;
      ctx.beginPath();
      ctx.moveTo(currentPath[0].x, currentPath[0].y);
      currentPath.forEach(point => {
        ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    }
    
    // Draw preview for shape tools
    if (isDrawing && startPoint && previewPoint && tool.type !== 'pen' && tool.type !== 'hand' && tool.type !== 'select' && tool.type !== 'text') {
      ctx.strokeStyle = tool.color;
      ctx.lineWidth = tool.size;
      ctx.setLineDash([5, 5]); // Dashed line for preview
      
      switch (tool.type) {
        case 'line':
          drawLine(ctx, startPoint, previewPoint, tool.color, tool.size);
          break;
          
        case 'arrow':
          drawArrow(ctx, startPoint, previewPoint, tool.color, tool.size);
          break;
          
        case 'rectangle':
          const width = previewPoint.x - startPoint.x;
          const height = previewPoint.y - startPoint.y;
          ctx.strokeRect(startPoint.x, startPoint.y, width, height);
          break;
          
        case 'circle':
          const radius = Math.sqrt(
            Math.pow(previewPoint.x - startPoint.x, 2) + Math.pow(previewPoint.y - startPoint.y, 2)
          ) / 2; // Use half the distance as radius
          const centerX = (startPoint.x + previewPoint.x) / 2;
          const centerY = (startPoint.y + previewPoint.y) / 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.stroke();
          break;
          
        case 'diamond':
          const diamondCenterX = (startPoint.x + previewPoint.x) / 2;
          const diamondCenterY = (startPoint.y + previewPoint.y) / 2;
          
          ctx.beginPath();
          ctx.moveTo(diamondCenterX, startPoint.y); // Top point
          ctx.lineTo(previewPoint.x, diamondCenterY);   // Right point
          ctx.lineTo(diamondCenterX, previewPoint.y);   // Bottom point
          ctx.lineTo(startPoint.x, diamondCenterY); // Left point
          ctx.closePath();
          ctx.stroke();
          break;
      }
      
      ctx.setLineDash([]); // Reset to solid line
    }

    // Draw selection box
    if (selectionBox && tool.type === 'select') {
      const width = selectionBox.end.x - selectionBox.start.x;
      const height = selectionBox.end.y - selectionBox.start.y;
      
      // Draw semi-transparent fill
      ctx.fillStyle = 'rgba(0, 170, 255, 0.1)';
      ctx.fillRect(selectionBox.start.x, selectionBox.start.y, width, height);
      
      // Draw dashed border
      ctx.strokeStyle = '#00aaff';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(selectionBox.start.x, selectionBox.start.y, width, height);
      ctx.setLineDash([]);
    }

    // Draw selection handles for selected objects
    if (selectedObjects.length > 0 && tool.type === 'select') {
      selectedObjects.forEach(id => {
        const obj = objects.find(o => o.id === id);
        if (obj) {
          drawSelectionHandles(ctx, obj);
        }
      });

    }
    
    // Restore transform
    ctx.restore();
  };

  const getCursorForHandle = (handle: string): string => {
    switch (handle) {
      case 'top-left':
      case 'bottom-right':
        return 'nw-resize';
      case 'top-right':
      case 'bottom-left':
        return 'ne-resize';
      default:
        return 'default';
    }
  };

  const drawSelectionHandles = (ctx: CanvasRenderingContext2D, obj: DrawnObject) => {
    ctx.fillStyle = '#00aaff';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;

    const handleSize = 6;
    let bounds: { x: number, y: number, width: number, height: number } | null = null;

    if (obj.points && obj.points.length > 0) {
      // For pen strokes, create bounding box
      const minX = Math.min(...obj.points.map(p => p.x));
      const maxX = Math.max(...obj.points.map(p => p.x));
      const minY = Math.min(...obj.points.map(p => p.y));
      const maxY = Math.max(...obj.points.map(p => p.y));
      bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    } else if (obj.start && obj.end) {
      // For shapes
      const minX = Math.min(obj.start.x, obj.end.x);
      const maxX = Math.max(obj.start.x, obj.end.x);
      const minY = Math.min(obj.start.y, obj.end.y);
      const maxY = Math.max(obj.start.y, obj.end.y);
      bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }

    if (bounds) {
      // Draw corner handles
      const corners = [
        { x: bounds.x, y: bounds.y }, // top-left
        { x: bounds.x + bounds.width, y: bounds.y }, // top-right
        { x: bounds.x, y: bounds.y + bounds.height }, // bottom-left
        { x: bounds.x + bounds.width, y: bounds.y + bounds.height }, // bottom-right
      ];

      corners.forEach(corner => {
        ctx.fillRect(corner.x - handleSize/2, corner.y - handleSize/2, handleSize, handleSize);
        ctx.strokeRect(corner.x - handleSize/2, corner.y - handleSize/2, handleSize, handleSize);
      });
    }
  };
  
  // Update redraw when objects change
  useEffect(() => {
    redrawCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, zoom, canvasOffset, selectedObjects, selectionBox, currentPath, previewPoint, resizing, theme]);

  // Auto-focus text input when it becomes visible
  useEffect(() => {
    if (isTyping && textInputRef.current) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 0);
    }
  }, [isTyping]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle keyboard shortcuts for selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (tool.type === 'select' && !isTyping) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          // Delete selected objects
          setObjects(prev => prev.filter(obj => !selectedObjects.includes(obj.id)));
          setSelectedObjects([]);
        } else if (e.key === 'Escape') {
          // Clear selection
          setSelectedObjects([]);
          setSelectionBox(null);
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
          // Select all
          e.preventDefault();
          setSelectedObjects(objects.map(obj => obj.id));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [tool.type, isTyping, selectedObjects, objects]);

  // Load saved data on component mount
  useEffect(() => {
    loadCanvasData();
  }, [loadCanvasData]);

  // Auto-save every 5 minutes
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (objects.length > 0) {
        saveCanvasData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(autoSaveInterval);
  }, [objects, saveCanvasData]);

  // Auto-save when objects change (debounced)
  useEffect(() => {
    const autoSaveTimeout = setTimeout(() => {
      if (objects.length > 0) {
        saveCanvasData();
      }
    }, 2000); // Save 2 seconds after last change

    return () => clearTimeout(autoSaveTimeout);
  }, [objects, saveCanvasData]);

  // Save on component unmount (before exiting)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (objects.length > 0) {
        saveCanvasData();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Final save on cleanup
      if (objects.length > 0) {
        saveCanvasData();
      }
    };
  }, [objects, saveCanvasData]);

  return (
    <div className={`drawing-canvas-container ${className} ${theme === 'light' ? 'light-theme' : 'dark-theme'}`}>
      {/* Drawing Toolbar */}
      <div className="drawing-toolbar">
        <div className="tool-group" ref={menuRef}>
          <button 
            className="menu-btn" 
            title="Menu"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            ☰
          </button>
          {isMenuOpen && (
            <div className="dropdown-menu">
              <div className="dropdown-section">
                <span className="dropdown-label">Theme:</span>
                <div className="dropdown-options">
                  <button 
                    className={`dropdown-option ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('light')}
                  >
                    ☀️ Light
                  </button>
                  <button 
                    className={`dropdown-option ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('dark')}
                  >
                    🌙 Dark
                  </button>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <button className="dropdown-action" onClick={clearCanvas}>
                🗑️ Clear Canvas
              </button>
            </div>
          )}
        </div>
        
        <div className="tool-group main-tools">
          <button 
            className={`lock-btn ${isLocked ? 'locked' : 'unlocked'}`}
            title={isLocked ? "Locked: Keep tool active after drawing" : "Unlocked: Auto-switch to selection after drawing"}
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? '🔒' : '🔓'}
          </button>
          <button
            className={`tool-btn ${tool.type === 'hand' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'hand' }))}
            title="Hand Tool"
          >
            <span className="tool-icon">✋</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'select' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'select' }))}
            title="Selection Tool (1)"
          >
            <span className="tool-icon">↖</span>
            <span className="tool-number">1</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'rectangle' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'rectangle' }))}
            title="Rectangle (2)"
          >
            <span className="tool-icon">▢</span>
            <span className="tool-number">2</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'diamond' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'diamond' }))}
            title="Diamond (3)"
          >
            <span className="tool-icon">◇</span>
            <span className="tool-number">3</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'circle' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'circle' }))}
            title="Circle (4)"
          >
            <span className="tool-icon">○</span>
            <span className="tool-number">4</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'arrow' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'arrow' }))}
            title="Arrow (5)"
          >
            <span className="tool-icon">→</span>
            <span className="tool-number">5</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'line' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'line' }))}
            title="Line (6)"
          >
            <span className="tool-icon">—</span>
            <span className="tool-number">6</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'pen' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'pen' }))}
            title="Pen (7)"
          >
            <span className="tool-icon">🖌️</span>
            <span className="tool-number">7</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'text' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'text' }))}
            title="Text (8)"
          >
            <span className="tool-icon">A</span>
            <span className="tool-number">8</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'image' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'image' }))}
            title="Image (9)"
          >
            <span className="tool-icon">🖼️</span>
            <span className="tool-number">9</span>
          </button>
          <button
            className={`tool-btn ${tool.type === 'eraser' ? 'active' : ''}`}
            onClick={() => setTool(prev => ({ ...prev, type: 'eraser' }))}
            title="Eraser (0)"
          >
            <span className="tool-icon">🧽</span>
            <span className="tool-number">0</span>
          </button>
        </div>
      </div>
      
      {/* Drawing Canvas */}
      <div className="canvas-container" style={{ position: 'relative' }}>
        <canvas
          ref={canvasRef}
          className="drawing-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          style={{
            cursor: tool.type === 'hand' ? 'grab' : 
                   tool.type === 'select' ? 
                     (resizing ? getCursorForHandle(resizing.handle) : 'default') : 
                   'crosshair'
          }}
        />
        
        {/* Text Input */}
        {isTyping && textPosition && textScreenPosition && (
          <div
            className="text-input-overlay"
            style={{
              position: 'absolute',
              left: textScreenPosition.x,
              top: textScreenPosition.y - 25, // Offset up to show instruction
              zIndex: 1000
            }}
          >
            <div className="text-input-container">
              <div className="text-input-instruction">
                Type text and press Enter to place it
              </div>
              <input
                ref={textInputRef}
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addText();
                  } else if (e.key === 'Escape') {
                    setIsTyping(false);
                    setTextInput('');
                    setTextPosition(null);
                    setTextScreenPosition(null);
                  }
                }}
                autoFocus
                className="text-input"
                style={{
                  color: tool.color,
                  fontSize: `${tool.size * 8}px`,
                  background: 'transparent',
                  border: '1px dashed #ffffff50',
                  outline: 'none',
                  padding: '2px'
                }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom Controls */}
      <div className="drawing-bottom-controls">
        <div className="zoom-controls">
          <button onClick={zoomOut} title="Zoom Out">−</button>
          <button onClick={resetZoom} title="Reset Zoom">{Math.round(zoom * 100)}%</button>
          <button onClick={zoomIn} title="Zoom In">+</button>
          <button
            className="action-btn undo-btn"
            onClick={undo}
            disabled={historyIndex <= 0}
            title="Undo"
          >
            ↶
          </button>
          <button
            className="action-btn redo-btn"
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            title="Redo"
          >
            ↷
          </button>
        </div>
      </div>
      
      {/* Clear Canvas Confirmation Modal */}
      {showClearConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Clear Canvas</h2>
              <button 
                className="modal-close"
                onClick={cancelClearCanvas}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-message">
                Are you sure you want to clear the entire canvas? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
                <button 
                  onClick={cancelClearCanvas}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: '#f8f9fa',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmClearCanvas}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#dc3545',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Clear Canvas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};